import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShoppingCart, Star, Heart } from 'lucide-react';
import { useWishlist } from '../contexts/WishlistContext';
import { toast } from '../hooks/use-toast';

const ProductCard = ({ product, onAddToCart }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const inWishlist = isInWishlist(product.id);
  
  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    if (inWishlist) {
      removeFromWishlist(product.id);
      toast({
        title: t('common.success'),
        description: t('wishlist.remove_from_wishlist'),
      });
    } else {
      addToWishlist(product);
      toast({
        title: t('common.success'),
        description: t('wishlist.title'),
      });
    }
  };

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border-2 hover:border-blue-200">
      <div className="relative" onClick={() => navigate(`/product/${product.id}`)}>
        {/* Badges */}
        <div className="absolute top-3 left-3 z-10 flex gap-2">
          {discount > 0 && (
            <Badge className="bg-red-500 hover:bg-red-600 text-white font-bold">
              -{discount}%
            </Badge>
          )}
          {!product.inStock && (
            <Badge variant="destructive" className="font-bold">
              {t('products.out_of_stock')}
            </Badge>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          className={`absolute top-3 right-3 z-10 bg-white rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
            inWishlist ? 'hover:bg-red-50' : 'hover:bg-red-50'
          }`}
          onClick={handleWishlistToggle}
        >
          <Heart className={`h-5 w-5 transition-colors ${
            inWishlist 
              ? 'text-red-500 fill-red-500' 
              : 'text-gray-600 hover:text-red-500'
          }`} />
        </button>

        {/* Product Image */}
        <div className="aspect-square overflow-hidden bg-gray-50 relative">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain bg-white"
          />
          {/* Multiple Images Indicator */}
          {product.images && product.images.length > 1 && (
            <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
              <span>📸</span>
              <span>{product.images.length}</span>
            </div>
          )}
        </div>
      </div>

      <CardContent className="p-4">
        {/* Brand */}
        <div className="text-sm font-semibold text-blue-600 mb-1">{product.brand}</div>

        {/* Product Name */}
        <h3
          className="font-semibold text-gray-900 mb-2 line-clamp-2 h-12 hover:text-blue-600 transition-colors"
          onClick={() => navigate(`/product/${product.id}`)}
        >
          {product.name}
        </h3>

        {/* Part Number */}
        <p className="text-xs text-gray-500 mb-2">{t('products.sku')}: {product.partNumber}</p>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-4 w-4 ${
                  i < Math.floor(product.rating)
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            ))}
          </div>
          <span className="text-sm text-gray-600">({product.reviews})</span>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-xl font-bold text-gray-900">OMR {product.price.toFixed(2)}</span>
          {product.originalPrice && (
            <span className="text-xs text-gray-500 line-through">
              OMR {product.originalPrice && product.originalPrice.toFixed(2)}
            </span>
          )}
        </div>

        {/* Add to Cart Button */}
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onAddToCart(product);
          }}
          disabled={!product.inStock}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all font-semibold"
        >
          <ShoppingCart className="mr-2 h-4 w-4" />
          {product.inStock ? t('products.add_to_cart') : t('products.out_of_stock')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
